import { ReactElement, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logMapBaseLayerListExpanded from '@alltrails/amplitude/events/logMapBaseLayerListExpanded';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationPathname } from '@alltrails/core/hooks/useClientNavigation';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Info from '@alltrails/denali/icons/Info';
import { TwoTabLayerType } from '../../types/LayerMenu';
import { getPage } from '../../utils/amplitudeHelpers';
import GridMoreBaseMapsItem from '../LayerMenuItem/GridMoreBaseMapsItem';
import styles from './GridLayerCards.module.scss';
import HeatmapPrivacyModal from './HeatmapPrivacyModal';

type DetailGroup = {
  id: string;
  title: string;
  items: React.ReactNode[];
};

type Props = {
  layerType: TwoTabLayerType;
  baseMapCards: ReactElement[] | null;
  conditionsCards?: ReactElement[] | null;
  detailCards: ReactElement[] | null;
  detailsGroups?: DetailGroup[];
  disableAnalytics?: boolean;
  heatmapCards?: ReactElement[] | null;
  layerOverlayCards?: ReactElement[] | null;
  terrainCards?: ReactElement[] | null;
  layerControl: ReactElement;
  onHeatmapPrivacyIconClick: () => void;
};

const GridLayerCards = ({
  layerType,
  baseMapCards,
  conditionsCards,
  detailCards,
  detailsGroups,
  disableAnalytics,
  heatmapCards,
  layerOverlayCards,
  terrainCards,
  layerControl,
  onHeatmapPrivacyIconClick
}: Props) => {
  const intl = useIntl();
  const pathname = useClientNavigationPathname();
  const hasOverlayCards = !!(heatmapCards || conditionsCards || layerOverlayCards || terrainCards);
  const [isHeatmapPrivacyModalOpen, setIsHeatmapPrivacyModalOpen] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const [shouldDisplayAllBaseMaps, setShouldDisplayAllBaseMaps] = useState(false);
  const page = getPage(pathname);

  const handleClickMore = useCallback(() => {
    if (!disableAnalytics) {
      logMapBaseLayerListExpanded({
        map_drawer_location: 'layers',
        map_location: page
      });
    }
    setShouldDisplayAllBaseMaps(true);
  }, [disableAnalytics, page]);

  const handleClick = useCallback(() => {
    if (isMobile) {
      onHeatmapPrivacyIconClick();
    } else {
      setIsHeatmapPrivacyModalOpen(true);
    }
  }, [isMobile, onHeatmapPrivacyIconClick]);

  return (
    <>
      <div className={styles.cardsContent}>
        {isMobile && layerControl}
        <>
          {layerType === 'layers' && (
            <>
              {baseMapCards && (
                <div className={styles.cardsSection}>
                  <Typography variant="heading200">
                    <FormattedMessage defaultMessage="Base map" />
                  </Typography>
                  <div className={styles.baseMapCardsSection}>
                    {shouldDisplayAllBaseMaps ? baseMapCards : baseMapCards?.slice?.(0, 3)}
                    {!!baseMapCards && baseMapCards?.length > 3 && !shouldDisplayAllBaseMaps && (
                      <GridMoreBaseMapsItem handleShowMore={handleClickMore} />
                    )}
                  </div>
                </div>
              )}
              {hasOverlayCards && (
                <div className={styles.cardsSection}>
                  <Typography variant="heading200">
                    <FormattedMessage defaultMessage="Overlays" />
                  </Typography>
                  <div className={styles.overlayCardsSection}>
                    {heatmapCards && (
                      <div className={styles.overlayCardContent}>
                        <div className={styles.tierSectionHeader}>
                          <div className={styles.heatmapTitle}>
                            <Typography variant="text150bold" component="div">
                              <FormattedMessage defaultMessage="Heatmaps" />
                            </Typography>
                            <IconButton
                              size="sm"
                              variant="ghost"
                              icon={{ Component: Info }}
                              onClick={handleClick}
                              title={intl.formatMessage({ defaultMessage: 'Heatmap privacy info' })}
                            />
                          </div>
                        </div>
                        <div className={styles.divider} />
                        <div className={styles.baseMapCardsSection}>{heatmapCards}</div>
                      </div>
                    )}
                    {conditionsCards ? (
                      <div className={styles.overlayCardContent}>
                        <div className={styles.tierSectionHeader}>
                          <Typography variant="text150bold" component="div">
                            <FormattedMessage defaultMessage="Conditions" />
                          </Typography>
                        </div>
                        <div className={styles.divider} />
                        <div className={styles.baseMapCardsSection}>{conditionsCards}</div>
                      </div>
                    ) : null}
                    {layerOverlayCards || terrainCards ? (
                      <div className={styles.overlayCardContent}>
                        <div className={styles.tierSectionHeader}>
                          <Typography variant="text150bold" component="div">
                            <FormattedMessage defaultMessage="Land & terrain" />
                          </Typography>
                        </div>
                        <div className={styles.divider} />
                        <div className={styles.baseMapCardsSection}>
                          {layerOverlayCards}
                          {terrainCards}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          )}
          {layerType === 'details' &&
            (detailsGroups ? (
              <div className={styles.detailGroupsSection}>
                {detailsGroups.map(group => (
                  <div key={group.id} className={styles.detailGroup}>
                    <Typography variant="heading200" className={styles.detailGroupTitle}>
                      {group.title}
                    </Typography>
                    <div className={styles.detailGroupItems}>
                      {group.items.map((item, index) => (
                        <div key={`${group.title}-item-${index}`}>{item}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.cardsSection}>{detailCards}</div>
            ))}
        </>
      </div>
      <HeatmapPrivacyModal isOpen={isHeatmapPrivacyModalOpen} onCloseRequest={() => setIsHeatmapPrivacyModalOpen(false)} />
    </>
  );
};

export default GridLayerCards;
